import { useMutation, useQuery } from '@apollo/client/react';

import SettingsContext, { type SettingOrigin } from './Context';

import { CreateCompanySettingDocument, GetCompanySettingsDocument } from '@/graphql/generated/graphql';
import { displayNotification } from '@/utils/notifications';

interface SettingsContextProviderProps {
  children: React.ReactNode;
}

const SettingsContextProvider = ({ children }: SettingsContextProviderProps) => {
  const [update] = useMutation(CreateCompanySettingDocument);
  const { data } = useQuery(GetCompanySettingsDocument);

  const handleCompanySettingChange = (origin: SettingOrigin, value: string) => {
    update({
      variables: {
        settingName: origin,
        settingValue: value,
      },
      update: (cache, { data: mutationData }) => {
        const existingData = cache.readQuery({
          query: GetCompanySettingsDocument,
        });

        if (!existingData?.getCompanySettings) return;

        const updatedSetting = mutationData?.createCompanySetting || {
          __typename: 'CompanySetting',
          name: origin,
          value: value,
        };

        const existingIndex = existingData.getCompanySettings.findIndex((setting) => setting.name === origin);

        let updatedList;
        if (existingIndex > -1) {
          updatedList = existingData.getCompanySettings.map((setting, idx) =>
            idx === existingIndex ? { ...setting, ...updatedSetting } : setting,
          );
        } else {
          updatedList = [...existingData.getCompanySettings, updatedSetting];
        }

        cache.writeQuery({
          query: GetCompanySettingsDocument,
          data: {
            ...existingData,
            getCompanySettings: updatedList,
          },
        });
      },
      onCompleted: () => displayNotification({ text: 'Setting updated successfully', type: 'success' }),
    });
  };

  const context = {
    handleCompanySettingChange,
    permissionMatrixLayoutValue:
      data?.getCompanySettings.find((setting) => setting.name === 'permission-matrix-layout')?.value || null,
    projectIssueLevelValue:
      data?.getCompanySettings.find((setting) => setting.name === 'project-issue-level')?.value || null,
    defaultCurrencyValue: data?.getCompanySettings.find((setting) => setting.name === 'default-currency')?.value || '£',
    defaultWorkShifts: data?.getCompanySettings.find((setting) => setting.name === 'work-shifts')?.value || null,
    companyHolidays: data?.getCompanySettings.find((setting) => setting.name === 'company_holidays')?.value || null,
  };

  return <SettingsContext.Provider value={context}>{children}</SettingsContext.Provider>;
};
export default SettingsContextProvider;
